import { useCallback, useEffect, useMemo, useState } from 'react';
import { collection, getDocs, deleteDoc, doc } from 'firebase/firestore';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import { db } from '../../../config/firebaseConfig';
import useInsuranceText from './insuranceText';
import { INSURANCE_TYPES, isExpiringSoon } from './insuranceUtils';

/**
 * Data of Sigorta Yönetimi: the Insurances records, the employees who have
 * no insurance yet (for adding), the "ending within a month" warning, the
 * filters, deleting and the Excel export. Firestore reads/writes and the
 * Excel layout are the old page's.
 */
const useInsurances = () => {
  const { label } = useInsuranceText();
  const [insurances, setInsurances] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedInsurance, setSelectedInsurance] = useState(null);
  const [filters, setFilters] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [showExpiring, setShowExpiring] = useState(false);
  const [onlyBES, setOnlyBES] = useState(false);

  const fetchInsurances = useCallback(async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'Insurances'));
      const insuranceData = querySnapshot.docs.map((docSnapshot) => ({ id: docSnapshot.id, ...docSnapshot.data() }));
      setInsurances(insuranceData);
    } catch (error) {
      console.error('Error fetching insurances:', error);
      toast.error(label('toast.loadError'));
    } finally {
      setIsLoading(false);
    }
  }, [label]);

  // Employees without an insurance record (the add form offers only them)
  const fetchEmployees = useCallback(async () => {
    try {
      const insuranceSnapshot = await getDocs(collection(db, 'Insurances'));
      const insuranceList = insuranceSnapshot.docs.map(docSnapshot => docSnapshot.data());

      const employeeSnapshot = await getDocs(collection(db, 'Employees'));
      const employeeData = employeeSnapshot.docs
        .map((docSnapshot) => ({
          id: docSnapshot.id,
          eName: docSnapshot.data().eName,
          eSurname: docSnapshot.data().eSurname,
          Active: docSnapshot.data().Active
        }))
        .filter(employee => {
          const hasInsurance = insuranceList.some(
            insurance => insurance.employeeId === employee.id
          );
          return !hasInsurance;
        });

      setEmployees(employeeData);
    } catch (error) {
      console.error('Error fetching employees:', error);
      toast.error(label('toast.employeesError'));
    }
  }, [label]);

  useEffect(() => {
    fetchInsurances();
    fetchEmployees();
    // Yalnızca açılışta
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const expiringInsurances = useMemo(() => insurances.filter(isExpiringSoon), [insurances]);

  // Bitiş tarihi 1 ay içinde olan sigortalar için açılışta uyarı (eskisi gibi)
  const [warned, setWarned] = useState(false);
  useEffect(() => {
    if (warned || insurances.length === 0) return;
    setWarned(true);
    if (expiringInsurances.length > 0) toast.warn(label('toast.expiring', { count: expiringInsurances.length }));
  }, [insurances, expiringInsurances, warned, label]);

  const filteredInsurances = useMemo(() => {
    let filtered = showExpiring ? expiringInsurances : insurances;

    // Search (employee name)
    if (searchTerm) {
      const lowercasedSearch = searchTerm.toLowerCase();
      filtered = filtered.filter(
        insurance =>
          (insurance.employeeName && insurance.employeeName.toString().toLowerCase().includes(lowercasedSearch))
      );
    }

    // Column filters (same rules as the old table)
    Object.keys(filters).forEach(key => {
      if (filters[key]) {
        if (key === 'hasBES') {
          filtered = filtered.filter(insurance =>
            String(Boolean(insurance[key])) === filters[key]
          );
        } else if (key.includes('.')) {
          const [parent, child] = key.split('.');
          filtered = filtered.filter(insurance =>
            String(insurance[parent]?.[child] || '').toLowerCase().includes(filters[key].toLowerCase())
          );
        } else {
          filtered = filtered.filter(insurance =>
            String(insurance[key] || '')
              .toLowerCase()
              .includes(filters[key].toLowerCase())
          );
        }
      }
    });

    if (onlyBES) filtered = filtered.filter((insurance) => insurance.hasBES);

    return [...filtered].sort((a, b) => String(a.employeeName || '').localeCompare(String(b.employeeName || ''), 'tr'));
  }, [insurances, expiringInsurances, filters, searchTerm, showExpiring, onlyBES]);

  // The selection follows refreshed data and is dropped once the record is gone
  useEffect(() => {
    if (!selectedInsurance || isLoading) return;
    const current = insurances.find((insurance) => insurance.id === selectedInsurance.id);
    if (!current) setSelectedInsurance(null);
    else if (current !== selectedInsurance) setSelectedInsurance(current);
  }, [insurances, selectedInsurance, isLoading]);

  const handleFilter = useCallback((column, value) => {
    setFilters((current) => ({ ...current, [column]: value }));
  }, []);

  const clearFilters = useCallback(() => {
    setFilters({});
    setSearchTerm('');
    setShowExpiring(false);
    setOnlyBES(false);
  }, []);

  // Asked first by the page (ConfirmDialog instead of window.confirm)
  const deleteInsurance = async (insurance) => {
    try {
      await deleteDoc(doc(db, 'Insurances', insurance.id));
      toast.success(label('toast.deleted'));
      setSelectedInsurance(null);
      fetchInsurances();
      fetchEmployees();
      return true;
    } catch (error) {
      console.error('Error deleting insurance:', error);
      toast.error(label('toast.deleteError'));
      return false;
    }
  };

  const downloadExcel = () => {
    const dataToExport = insurances.map((insurance) => ({
      'Çalışan Adı': insurance.employeeName,
      'BES Durumu': insurance.hasBES ? 'Var' : 'Yok',
      'Ferdi Kaza Başlangıç': insurance.accidentInsurance?.startDate,
      'Ferdi Kaza Bitiş': insurance.accidentInsurance?.endDate,
      'Tamamlayıcı Sağlık Başlangıç': insurance.complementaryHealth?.startDate,
      'Tamamlayıcı Sağlık Bitiş': insurance.complementaryHealth?.endDate,
      'Malpraktis Başlangıç': insurance.malpractice?.startDate,
      'Malpraktis Bitiş': insurance.malpractice?.endDate,
      'Maliyet': insurance.cost,
    }));

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Insurances');
    XLSX.writeFile(workbook, 'SigortaListesi.xlsx');

    toast.success(label('toast.excelDone'));
  };

  const stats = useMemo(() => ({
    total: insurances.length,
    withBES: insurances.filter((insurance) => insurance.hasBES).length,
    expiring: expiringInsurances.length,
    totalCost: insurances.reduce((sum, insurance) => sum + (parseFloat(insurance.cost) || 0), 0),
  }), [insurances, expiringInsurances]);

  const refresh = () => {
    fetchInsurances();
    fetchEmployees();
  };

  return {
    insurances,
    employees,
    isLoading,
    filteredInsurances,
    stats,
    selectedInsurance,
    setSelectedInsurance,
    filters,
    handleFilter,
    clearFilters,
    searchTerm,
    setSearchTerm,
    showExpiring,
    setShowExpiring,
    onlyBES,
    setOnlyBES,
    deleteInsurance,
    downloadExcel,
    refresh,
    insuranceTypes: INSURANCE_TYPES,
  };
};

export default useInsurances;
